
import React from 'react';
import { motion, Variants } from 'framer-motion';

interface SectionTransitionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  id?: string;
  threshold?: number;
  type?: 'fade' | 'slide-up' | 'slide-in' | 'scale';
}

/**
 * A component that wraps sections to provide smooth transition animations when they enter the viewport.
 */
const SectionTransition: React.FC<SectionTransitionProps> = ({
  children,
  className = '',
  delay = 0,
  duration = 0.5,
  id,
  threshold = 0.15,
  type = 'fade',
}) => {
  // Animation variants based on the type
  const getVariants = (): Variants => {
    switch (type) {
      case 'slide-up':
        return {
          hidden: { opacity: 0, y: 20 },
          visible: { 
            opacity: 1, 
            y: 0, 
            transition: { 
              duration: duration, 
              delay: delay,
              ease: "easeOut"
            }
          }
        };
      case 'slide-in':
        return {
          hidden: { opacity: 0, x: -20 },
          visible: { 
            opacity: 1, 
            x: 0, 
            transition: { 
              duration: duration, 
              delay: delay,
              ease: "easeOut"
            }
          }
        };
      case 'scale':
        return {
          hidden: { opacity: 0, scale: 0.97 },
          visible: { 
            opacity: 1,
            scale: 1, 
            transition: { 
              duration: duration, 
              delay: delay,
              ease: "easeOut"
            }
          }
        };
      case 'fade':
      default:
        return {
          hidden: { opacity: 0 },
          visible: { 
            opacity: 1, 
            transition: { 
              duration: duration, 
              delay: delay,
              ease: "easeOut"
            }
          }
        };
    }
  };

  return (
    <motion.div
      id={id}
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: threshold }}
      variants={getVariants()}
    >
      {children}
    </motion.div>
  );
};

export default SectionTransition;
